import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import {
  GeolocationType,
  Journey,
  JourneyKind,
  JourneyState,
} from 'interfaces/journey';
import Api from 'util/Api';
import api from 'constants/api';
import { useSelector } from 'react-redux';
import { authProfile } from 'redux/auth/selectors';

export type ScheduleStatus = 'expired' | 'ongoing' | 'pending' | 'none';

interface GetJourneysQueries {
  driverId?: number | null;
  kind?: JourneyKind;
  state?: JourneyState[];
  page?: number | null;
  size?: number | null;
  date?: Dayjs;
  search?: string;
  scheduleStatus?: ScheduleStatus;
}

export interface GetJourneysRequest extends GetJourneysQueries {
  distributionCenterID?: number | null;
  dateType?: 'creation' | 'started';
  from?: string;
  to?: string;
}

export interface GetJourneysResponse {
  message?: string;
  page?: number;
  total_elements?: number;
  journeys?: Journey[];
}

const getRoutes = async (queries?: GetJourneysRequest) => {
  const { state, ...rest } = queries || {};

  const formattedParams = {
    ...rest,
    state: Array.isArray(state) ? state.join(',') : state,
  };

  const cleanParams = Object.fromEntries(
    Object.entries(formattedParams).filter(
      ([_, v]) => v !== '' && v !== undefined && v !== null,
    ),
  );

  const response = await Api.apiAxios.get<GetJourneysResponse>(
    api.journeys.list,
    {
      params: cleanParams,
    },
  );

  if (!Api.isSuccessResponse(response)) {
    throw new Error(response.data.message);
  }

  return response.data;
};

export const useTrackingRoutes = (queries?: GetJourneysQueries) => {
  const profile = useSelector(authProfile);
  const query = useMemo(() => {
    return {
      page: queries?.page ? Number(queries.page) : 0,
      size: queries?.size ? Number(queries.size) : 10,
      driverId: queries?.driverId,
      kind: queries?.kind,
      from: queries?.date
        ? dayjs.utc(queries.date.startOf('day')).format()
        : '',
      to: queries?.date ? dayjs.utc(queries.date.endOf('day')).format() : '',
      dateType: 'creation',
      state:
        queries?.state && queries?.state?.length > 0
          ? queries?.state
          : [
              JourneyState.Assigned,
              JourneyState.Offered,
              JourneyState.Accepted,
              JourneyState.Started,
              JourneyState.Transfering,
            ],
      search: queries?.search,
      scheduleStatus: queries?.scheduleStatus,
      distributionCenterID: profile?.paquerypoint?.id,
    } as GetJourneysRequest;
  }, [queries, profile]);

  return useQuery<GetJourneysResponse, Error>({
    queryKey: ['trackingRoutes', query],
    queryFn: () => getRoutes(query),
    retry: 1,
    staleTime: 5000,
    enabled: !!profile?.paquerypoint?.id,
  });
};

export const usePrepRoutes = (queries?: GetJourneysQueries) => {
  const profile = useSelector(authProfile);
  const query = useMemo(() => {
    return {
      page: queries?.page ? Number(queries.page) : 0,
      size: queries?.size ? Number(queries.size) : 10,
      driverId: queries?.driverId,
      kind: queries?.kind,
      from: queries?.date
        ? dayjs.utc(queries.date.startOf('day')).format()
        : '',
      to: queries?.date ? dayjs.utc(queries.date.endOf('day')).format() : '',
      dateType: 'creation',
      state: [JourneyState.Draft],
      search: queries?.search,
      distributionCenterID: profile?.paquerypoint?.id,
    } as GetJourneysRequest;
  }, [queries, profile]);

  return useQuery<GetJourneysResponse, Error>({
    queryKey: ['prepRoutes', query],
    queryFn: () => getRoutes(query),
    retry: 1,
    staleTime: 5000,
    enabled: !!profile?.paquerypoint?.id,
  });
};

export const isEmptyDraftJourney = (journey: Journey) =>
  !journey.totalPackages &&
  !journey.driverId &&
  !journey.driver?.id &&
  !journey.driverName &&
  (!journey.originAddress?.label ||
    journey.originAddress.label === GeolocationType.DistributionCenter);

export const useHistoryRoutes = (queries?: GetJourneysQueries) => {
  const profile = useSelector(authProfile);
  const query = useMemo(() => {
    return {
      page: queries?.page ? Number(queries.page) : 0,
      size: queries?.size ? Number(queries.size) : 10,
      driverId: queries?.driverId,
      kind: queries?.kind,
      from: queries?.date
        ? dayjs.utc(queries.date.startOf('day')).format()
        : '',
      to: queries?.date ? dayjs.utc(queries.date.endOf('day')).format() : '',
      dateType: 'started',
      state: [JourneyState.Finished],
      search: queries?.search,
      distributionCenterID: profile?.paquerypoint?.id,
    } as GetJourneysRequest;
  }, [queries, profile]);

  return useQuery<GetJourneysResponse, Error>({
    queryKey: ['historyRoutes', query],
    queryFn: () => getRoutes(query),
    retry: 1,
    staleTime: 5000,
    enabled: !!profile?.paquerypoint?.id,
  });
};
